import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000CriteriaLookupConfig } from '../u2000Core/components/tables/basic/U2000BasicTableList';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import type { U2000AutoCompleteSearchResult } from '../u2000Core/types/U2000AutoCompleteSearchResult';
import { u2000ExtractAutoCompleteResults } from '../u2000Core/utils/u2000ExtractAutoCompleteResults';
import { u3125FormatInvoiceNumberDisplay } from '../u3125Facture/U3125Service';
import {
  doFindEmployee,
  doFindInvoice,
  doFindProject,
  luHandleAppiError,
  type U3134AutoCompletCriterias,
  type U3134FindEmployeeItem,
  type U3134FindInvoiceItem,
  type U3134FindProjectItem,
} from './U3134Service';
import { NS } from './U3134';

/** Recherche projet via POST /U3134/findProject (criteriaLookup BL-0058). */
export function useU3134FindProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3134AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3134FindProjectItem>> => {
      try {
        const response = await doFindProject(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3134FindProjectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

/** Recherche n° facture via POST /U3134/findInvoice (criteriaLookup BL-0058). */
export function useU3134FindInvoiceHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3134AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3134FindInvoiceItem>> => {
      try {
        const response = await doFindInvoice(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3134FindInvoiceItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

/** Recherche employé via POST /U3134/findEmployee (criteriaLookup BL-0426). */
export function useU3134FindEmployeeHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3134AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3134FindEmployeeItem>> => {
      try {
        const response = await doFindEmployee(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3134FindEmployeeItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

export function u3134InvoiceLookupItemToValue(item: unknown, _config: U2000CriteriaLookupConfig): string {
  const row = item as U3134FindInvoiceItem;
  if (row?.invoiceNumber == null) {
    return '';
  }
  return String(row.invoiceNumber).trim();
}

export function u3134InvoiceLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  const row = item as U3134FindInvoiceItem;
  return u3125FormatInvoiceNumberDisplay(row?.invoiceNumber ?? null);
}

export function u3134InvoiceLookupItemToSelectedDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  return u3134InvoiceLookupItemToDisplay(item, _config);
}
